import { ReactNode } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';

import {
  Alert,
  AlertIcon,
  Button,
  Flex,
  Heading,
  List,
  ListItem,
  Tag,
  TagLabel,
  Text,
  useDisclosure,
} from '@chakra-ui/react';

import { MarkdownProps } from '~/components/markdown';
import { Modal } from '~/components/modal';
import { trackEvent } from '~/features/analytics/track-event';

import { useExperimentalSegmentEvents } from '../hooks';
import { CheckedIcon, ExclamationIcon, ICON_SIZE } from '../icons';
import { pluralize } from '../utils';

import { StaticProgramDetailsSectionProps } from './program-details-section';

const Markdown = dynamic<MarkdownProps>(() => import('~/components/markdown').then((mod) => mod.Markdown));

const NUMBER_OF_PREREQUISITES_TO_DISPLAY = 5 as const;

export type ProgramPrerequisitesSubsectionProps = {
  prerequisites: StaticProgramDetailsSectionProps['prerequisites'];
  additionalRequirements: string | null;
  programKey: string;
};

export function ProgramPrerequisitesSubsection({
  additionalRequirements,
  prerequisites,
  programKey,
}: ProgramPrerequisitesSubsectionProps) {
  const { isOpen, onOpen, onClose } = useDisclosure({
    onOpen: () =>
      trackEvent({
        type: 'click',
        name: 'Product Page CTA Clicked',
        label: 'prerequisites_clicked',
        additionalAttributes: {
          product_ids: [programKey],
        },
      }),
  });
  return (
    <Flex as='section' flexDirection='column' id='prerequisites'>
      <Flex flexWrap='wrap' gap={6} justifyContent='space-between'>
        <Heading
          as='h2'
          size={{
            base: 'heading-6',
            md: 'heading-5',
          }}
        >
          Prerequisites
        </Heading>
        <Flex alignItems='center' gap={2}>
          <CheckedIcon color='supportGray.40' height={ICON_SIZE} width={ICON_SIZE} />
          <Text
            whiteSpace='nowrap'
            fontSize={{
              base: 'sm',
              md: 'md',
            }}
          >
            {pluralize(prerequisites.length, 'prerequisite', 'prerequisites')}
          </Text>
        </Flex>
        <PrerequisitesContent
          additionalRequirements={additionalRequirements}
          maxPrerequisitesToDisplay={NUMBER_OF_PREREQUISITES_TO_DISPLAY}
          prerequisites={prerequisites}
          programKey={programKey}
          renderShowMorePrerequisitesSlot={({ remainingPrerequisiteCount }) => {
            if (remainingPrerequisiteCount === 0) {
              return null;
            }
            return (
              <>
                <Button
                  color='primary.blue'
                  display='inline-block'
                  padding={0}
                  size='sm'
                  textDecoration='underline'
                  variant='link'
                  onClick={onOpen}
                >
                  +{remainingPrerequisiteCount} More
                </Button>
                <Modal
                  isOpen={isOpen}
                  size='3xl'
                  title={
                    <Heading as='header' size='heading-5'>
                      Prerequisites
                    </Heading>
                  }
                  onClose={onClose}
                >
                  <PrerequisitesContent
                    additionalRequirements={additionalRequirements}
                    prerequisites={prerequisites}
                    programKey={programKey}
                  />
                </Modal>
              </>
            );
          }}
        />
      </Flex>
    </Flex>
  );
}

function PrerequisitesContent({
  prerequisites,
  programKey,
  additionalRequirements,
  maxPrerequisitesToDisplay = prerequisites.length,
  renderShowMorePrerequisitesSlot,
}: {
  prerequisites: StaticProgramDetailsSectionProps['prerequisites'];
  programKey: string;
  additionalRequirements: string | null;
  maxPrerequisitesToDisplay?: number;
  renderShowMorePrerequisitesSlot?: ({
    remainingPrerequisiteCount,
  }: {
    remainingPrerequisiteCount: number;
  }) => ReactNode;
}) {
  const isExperimentalTrackEventsEnabled = useExperimentalSegmentEvents('dino')?.variant === 'on';
  const showMorePrerequisitesSlot = renderShowMorePrerequisitesSlot?.({
    remainingPrerequisiteCount:
      prerequisites.length > maxPrerequisitesToDisplay ? prerequisites.length - maxPrerequisitesToDisplay : 0,
  });

  return (
    <>
      <Flex flexDirection='column' gap={4}>
        {prerequisites.length > 0 ? (
          <>
            <Text>Prior to enrolling, you should have the following knowledge:</Text>
            <List alignItems='center' display='flex' flexWrap='wrap' gap={2}>
              {prerequisites.slice(0, maxPrerequisitesToDisplay).map((prereq) => {
                return (
                  <Tag key={prereq.label} as={ListItem} colorScheme='blue' size='lg'>
                    <TagLabel
                      as={prereq.url ? Link : undefined}
                      color={prereq.url ? 'supportBlue.80' : undefined}
                      fontWeight='600'
                      href={prereq.url ?? undefined}
                      textDecoration={prereq.url ? 'underline' : undefined}
                      textTransform='capitalize'
                      onClick={() => {
                        if (isExperimentalTrackEventsEnabled) {
                          trackEvent({
                            name: 'Product Page CTA Clicked',
                            type: 'click',
                            label: 'prerequisite_clicked',
                            value: prereq.label,
                            additionalAttributes: {
                              product_ids: [programKey],
                            },
                          });
                        }
                      }}
                    >
                      {prereq.label}
                    </TagLabel>
                  </Tag>
                );
              })}
              {showMorePrerequisitesSlot && <ListItem key='show-more-prereqs'>{showMorePrerequisitesSlot}</ListItem>}
            </List>
          </>
        ) : (
          <Alert
            bgColor='supportBlue.10'
            border='1px solid'
            borderColor='supportBlue.20'
            borderRadius='8px'
            boxShadow='unset'
            padding='16px 20px'
            status='info'
            variant='subtle'
          >
            <AlertIcon as={ExclamationIcon} bgColor='inherit' color='supportGray.40' />
            No prior technical experience required.
          </Alert>
        )}
        <Text>
          You will {prerequisites.length > 0 ? 'also' : ''} need to be able to communicate fluently and professionally
          in <Text as='strong'>written and spoken English</Text>.
        </Text>
        {!!additionalRequirements && (
          <Alert
            bgColor='supportGray.10'
            border='1px solid'
            borderColor='supportGray.20'
            borderLeft='2px solid'
            borderLeftColor='supportBlue.70'
            borderRadius='0px 8px 8px 0px'
            boxShadow='none'
            gap={3}
            padding={3}
            status='info'
            variant='subtle'
          >
            <Flex
              alignItems='center'
              bgColor='supportBlue.20'
              borderRadius='50%'
              display='flex'
              flexShrink={0}
              gap={2}
              height='32px'
              justifyContent='center'
              width='32px'
            >
              <ExclamationIcon color='supportBlue.70' height={ICON_SIZE} width={ICON_SIZE} />
            </Flex>
            <Markdown markdownText={additionalRequirements} />
          </Alert>
        )}
      </Flex>
    </>
  );
}
